/**
 * Sobre: qual versão está no ar, de quando ela é, e para quem ligar quando algo não vai.
 *
 * É a sétima etapa da parte 4, e ela ficou devendo desde a parte 3. O que ela mostra não é
 * novidade nenhuma: é a mesma linha que o log do servidor escreve ao subir, posta numa tela,
 * **para quem não tem a linha de comando na mão**. Hoje, para saber se o que subiu foi o que
 * você mandou, alguém precisa abrir um terminal.
 *
 * Três decisões desta tela:
 *
 * **Ela funciona sem sessão**, como a página do link de trocar a senha. A hora em que se
 * pergunta "está no ar? é a versão nova? para quem eu ligo?" é justamente a hora em que não se
 * consegue entrar. E a rota que ela chama responde até com o banco fora do ar.
 *
 * **Ela compara as duas versões.** A do servidor vem da resposta; a do painel foi escrita dentro
 * do arquivo montado, na hora de montar. Quando as duas discordam, quem está velho é o
 * navegador, que guardou a página de antes, e a tela diz o que fazer. Sem isso a pessoa leria um
 * número certo numa tela errada, e o defeito pareceria ser do servidor.
 *
 * **O servidor calado também é resposta.** Se a chamada falhar, a tela diz que não houve
 * resposta e mostra o que o navegador sabe sozinho, que é a versão da própria tela. Página em
 * branco, aqui, esconderia a única informação que importava.
 */
import { useEffect, useState, type JSX } from 'react'
import { servidor, type Sobre as DadosDoSobre } from '../api'
import { NOME_DO_SISTEMA } from '../identidade'
import { VERSAO_DO_PAINEL } from '../versao'

/** `2026-10-07T18:32:00Z` vira `07/10/2026 às 15h32`, no relógio de quem está olhando. */
function diaEHora(iso: string | null): string | null {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  const dois = (n: number): string => String(n).padStart(2, '0')
  return (
    `${dois(d.getDate())}/${dois(d.getMonth() + 1)}/${d.getFullYear()}` +
    ` às ${dois(d.getHours())}h${dois(d.getMinutes())}`
  )
}

/**
 * O endereço de quem cuida, como link quando dá para fazer link.
 *
 * E-mail vira `mailto:`, endereço com `http` vira link, e o resto (um telefone, por exemplo)
 * fica como texto. Quem hospeda escreve o que quiser na variável e a tela não exige formato.
 */
function QuemCuida({ endereco }: { endereco: string }): JSX.Element {
  if (/^https?:\/\//.test(endereco)) {
    return <a href={endereco}>{endereco}</a>
  }
  // Um e-mail, e não um texto com arroba dentro: uma arroba, e nada de espaço.
  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(endereco)) {
    return <a href={`mailto:${endereco}`}>{endereco}</a>
  }
  return <>{endereco}</>
}

type Estado =
  | { fase: 'abrindo' }
  | { fase: 'pronto'; dados: DadosDoSobre }
  /** O servidor não respondeu. Isso também é informação, e a tela mostra o que sabe. */
  | { fase: 'sem-resposta' }

export function Sobre(): JSX.Element {
  const [estado, setEstado] = useState<Estado>({ fase: 'abrindo' })

  useEffect(() => {
    let vale = true
    servidor
      .sobre()
      .then((dados) => {
        if (vale) setEstado({ fase: 'pronto', dados })
      })
      .catch(() => {
        if (vale) setEstado({ fase: 'sem-resposta' })
      })
    return () => {
      vale = false
    }
  }, [])

  const dados = estado.fase === 'pronto' ? estado.dados : null
  const telaVelha = dados !== null && dados.versao !== VERSAO_DO_PAINEL

  return (
    <main className="entrar sobre" data-fase={estado.fase}>
      <h1>{dados?.sistema ?? NOME_DO_SISTEMA}</h1>
      <p className="subtitulo">Sobre</p>

      {estado.fase === 'abrindo' && <p className="recado aguarde">Perguntando ao servidor...</p>}

      {estado.fase === 'sem-resposta' && (
        <>
          <p className="recado" role="alert">
            O servidor não respondeu. Pode ser a internet daqui, ou pode ser ele.
          </p>
          <dl>
            <dt>Esta tela</dt>
            <dd>versão {VERSAO_DO_PAINEL}</dd>
            <dt>Você está em</dt>
            <dd>{window.location.host}</dd>
          </dl>
        </>
      )}

      {dados !== null && (
        <>
          <dl>
            <dt>Versão</dt>
            <dd>{dados.versao}</dd>

            <dt>De quando é</dt>
            <dd>{diaEHora(dados.de_quando_e) ?? 'não foi possível descobrir'}</dd>

            <dt>No ar desde</dt>
            <dd>{diaEHora(dados.no_ar_desde) ?? 'não foi possível descobrir'}</dd>

            <dt>Você está em</dt>
            <dd>{window.location.host}</dd>

            <dt>E-mails</dt>
            <dd>
              {dados.emails_por === 'resend'
                ? 'saem pela Resend'
                : 'não saem. Ficam gravados numa pasta deste servidor'}
            </dd>

            <dt>Quem cuida</dt>
            <dd>
              {dados.quem_cuida === null ? (
                <span className="nao-tem">ninguém configurou</span>
              ) : (
                <QuemCuida endereco={dados.quem_cuida} />
              )}
            </dd>
          </dl>

          {telaVelha && (
            <p className="explicacao atencao">
              Esta página é da versão {VERSAO_DO_PAINEL}, e o servidor já está na {dados.versao}.
              Feche o painel e abra de novo, para o navegador largar a cópia velha.
            </p>
          )}

          <p className="rodape">{dados.aviso_de_titularidade}</p>
        </>
      )}

      <a className="como-link" href="/">
        Voltar
      </a>
    </main>
  )
}
